import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useVisibilityAwareInterval } from '../hooks/useVisibilityAwareInterval'
import { CheckCircle, AlertTriangle, Clock, RefreshCw, History } from 'lucide-react'
import { ComponentUptime, type StatusComponentsData } from '../components/status/ComponentUptime'
import { StatusHeader } from '../components/status/StatusHeader'
import { useStatusHref, type StatusBranding, type StatusUpdate } from '../components/status/statusShared'

interface StatusIncident {
  id: string
  title: string
  severity: string
  status: string
  triggered_at: string
  resolved_at?: string
  updates?: StatusUpdate[]
}

interface StatusPageData {
  org_name: string
  branding: StatusBranding
  generated_at: string
  active_incidents: StatusIncident[]
}

const SEVERITY_COLOR: Record<string, string> = {
  critical: 'text-red-700 bg-red-50 border-red-200',
  high:     'text-orange-700 bg-orange-50 border-orange-200',
  medium:   'text-yellow-700 bg-yellow-50 border-yellow-200',
  low:      'text-blue-600 bg-blue-50 border-blue-200',
}

const SEVERITY_DOT: Record<string, string> = {
  critical: 'bg-red-500',
  high:     'bg-orange-500',
  medium:   'bg-yellow-500',
  low:      'bg-blue-500',
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

function IncidentRow({ incident }: { incident: StatusIncident }) {
  const href = useStatusHref()
  const dotClass = SEVERITY_DOT[incident.severity] ?? 'bg-gray-400'
  const badgeClass = SEVERITY_COLOR[incident.severity] ?? 'text-gray-600 bg-gray-50 border-gray-200'
  // Updates arrive oldest first; show the newest first.
  const updates = [...(incident.updates ?? [])].reverse()

  return (
    <div className="flex items-start gap-4 py-4 border-b border-gray-100 last:border-0">
      <div className="mt-1.5 flex-shrink-0">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${dotClass} animate-pulse`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${badgeClass}`}>
            {incident.severity}
          </span>
        </div>
        <Link to={href(`/incidents/${incident.id}`)} className="block text-sm font-medium text-gray-900 mt-1 hover:underline">
          {incident.title}
        </Link>
        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {timeAgo(incident.triggered_at)}
          </span>
        </div>
        {updates.length > 0 && (
          <div className="mt-2 space-y-2">
            {updates.map((update, i) => (
              <div key={i} className="text-sm text-gray-700">
                <span className="text-xs text-gray-500">
                  {update.label && <span className="font-medium text-gray-700">{update.label} · </span>}
                  {formatTime(update.created_at)}
                </span>
                <p className="whitespace-pre-wrap">{update.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export function StatusPage() {
  const href = useStatusHref()
  const [data, setData] = useState<StatusPageData | null>(null)
  const [components, setComponents] = useState<StatusComponentsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())

  const fetch = useCallback(async () => {
    // Uptime history is optional: the page still renders incidents without it.
    window.fetch('/api/v1/status/components')
      .then(res => (res.ok ? res.json() : null))
      .then((json: StatusComponentsData | null) => setComponents(json?.components ? json : null))
      .catch(() => setComponents(null))
    try {
      const res = await window.fetch('/api/v1/status')
      if (!res.ok) throw new Error('Failed to load status')
      const json: StatusPageData = await res.json()
      setData(json)
      setError(null)
      setLastRefresh(new Date())
    } catch {
      setError('Unable to load status information.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetch() }, [fetch])
  useVisibilityAwareInterval(fetch, 30_000)

  const isHealthy = !loading && !error && data && data.active_incidents.length === 0
  const branding = data?.branding
  const accentColor = branding?.accent_color || '#2563eb'

  useEffect(() => {
    if (branding?.title) document.title = branding.title
  }, [branding?.title])

  return (
    <div className="min-h-screen bg-gray-50">
      <StatusHeader branding={branding} orgName={data?.org_name}>
        <button
          onClick={fetch}
          className="p-2 border rounded-lg flex-shrink-0 transition-opacity hover:opacity-75"
          style={{ color: accentColor, borderColor: accentColor }}
          title="Refresh"
          aria-label="Refresh status"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </StatusHeader>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Overall health banner */}
        {!loading && !error && (
          <div className={`flex items-center gap-3 px-4 py-3 rounded-lg ${
            isHealthy
              ? 'bg-green-50 border border-green-200'
              : 'bg-red-50 border border-red-200'
          }`}>
            {isHealthy ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-sm font-medium text-green-800">All systems operational</span>
              </>
            ) : (
              <>
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <span className="text-sm font-medium text-red-800">
                  {data?.active_incidents.length} active incident{data && data.active_incidents.length !== 1 ? 's' : ''}
                </span>
              </>
            )}
          </div>
        )}

        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-white rounded-lg border border-gray-200 animate-pulse" />
            ))}
          </div>
        )}

        {error && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-500">
            {error}
          </div>
        )}

        {data && !loading && (
          <>
            {/* Active incidents come first: they are what visitors are looking for. */}
            {data.active_incidents.length > 0 && (
              <section>
                <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
                  Active Incidents
                </h2>
                <div className="bg-white border border-gray-200 rounded-lg px-4">
                  {data.active_incidents.map(inc => (
                    <IncidentRow key={inc.id} incident={inc} />
                  ))}
                </div>
              </section>
            )}

            {components && <ComponentUptime data={components} />}

            <div className="flex justify-center">
              <Link to={href('/history')} className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:underline">
                <History className="w-4 h-4" />
                View history
              </Link>
            </div>
          </>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-gray-600">
          Auto-refreshes every 30s · Last updated {lastRefresh.toLocaleTimeString()}
        </p>
      </div>
    </div>
  )
}
